import { useState, useEffect, useRef } from 'react';
import componentStyles from './inDepthBetCard.module.css';
import { canViewPropAnalyzer } from '../../utils/scoutingReportConfig';
import { isFeatureSupported } from '../../utils/featureUtil';
import BetRecommendation from './betRecommendation';
import PropBetRecommendation from './propBetRecommendation';
import HistoricalMatchup from './historicalMatchup';
import GameLogs from './gameLogs';
import GameInfoCard from './gameInfoCard';
import InsightBox from './insightBox';
import PlayerPropAnalyzer from './playerPropAnalyzer';
import InjuryReport from './injuryReport';

const ScoutingReport = ({ betCardData, inDepthBetCardTeamStats, inDepthBetCardMatchupInsights, inDepthBetCardMatchupInjuries }) => {
    // State to track selected bet type (team or prop)
    const [selectedBetType, setSelectedBetType] = useState('team');
    const pillButtonContainerRef = useRef(null); // Ref for pill button container
    const isPGA = ['pga', 'pga_tour', 'golf'].includes(String(betCardData?.sportType || '').toLowerCase());

    // Check if scouting reports are available
    const isScoutingReportAvailable = betCardData['scouting_report'] && 
        betCardData['scouting_report'] instanceof Object && 
        Object.keys(betCardData['scouting_report']).length > 0;
    
    const isPropBetReportAvailable = betCardData['prop_bet_report'] && 
        betCardData['prop_bet_report'] instanceof Object && 
        Object.keys(betCardData['prop_bet_report']).length > 0;

    // Check if new insight fields are available
    const hasKeyInsights = betCardData['scouting_report'] && (
        betCardData['scouting_report']['keyInsightHeadline'] ||
        betCardData['scouting_report']['featuredProp'] ||
        betCardData['scouting_report']['matchupAdvantage'] ||
        betCardData['scouting_report']['lineMovementSummary'] ||
        betCardData['scouting_report']['expertPickStatus']
    );

    // Auto-select the first available pill (in priority order):
    // team (if available) -> prop (if available) -> historicalMatchup -> gameLogs -> playerProps
    useEffect(() => {
        const available = [];
        if (isPGA) {
            if (isPropBetReportAvailable) available.push('prop');
        } else {
            if (isScoutingReportAvailable) available.push('team');
            if (isPropBetReportAvailable) available.push('prop');
            if (true) available.push('historicalMatchup');
            if (true) available.push('gameLogs');
            if (true) available.push('playerProps');
        }
        if (!available.includes(selectedBetType)) {
            setSelectedBetType(available[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isScoutingReportAvailable, isPropBetReportAvailable, isPGA]);

    // Scroll to selected pill button when component loads or selection changes
    useEffect(() => {
        if (pillButtonContainerRef.current && selectedBetType) {
            const scrollToSelectedPill = () => {
                const pillButtons = pillButtonContainerRef.current.querySelectorAll('button');
                let selectedButton = null;
                
                // Find the button for the selected bet type
                pillButtons.forEach(button => {
                    if (button.textContent === getPillLabel(selectedBetType)) {
                        selectedButton = button;
                    }
                });
                
                if (selectedButton) {
                    const container = pillButtonContainerRef.current;
                    const buttonLeft = selectedButton.offsetLeft;
                    const buttonWidth = selectedButton.offsetWidth;
                    const containerWidth = container.offsetWidth;
                    const currentScrollLeft = container.scrollLeft;
                    
                    // Calculate distances to left and right edges
                    const distanceToLeft = buttonLeft - currentScrollLeft;
                    const distanceToRight = (buttonLeft + buttonWidth) - (currentScrollLeft + containerWidth);
                    
                    // Determine if we need to scroll and in which direction
                    let targetScrollLeft = currentScrollLeft;
                    
                    if (distanceToLeft < 0) {
                        // Button is to the left of visible area - scroll left
                        targetScrollLeft = Math.max(0, buttonLeft - 20); // 20px padding from left edge
                    } else if (distanceToRight > 0) {
                        // Button is to the right of visible area - scroll right
                        targetScrollLeft = buttonLeft + buttonWidth - containerWidth + 20; // 20px padding from right edge
                    } else if (Math.abs(distanceToLeft) < containerWidth * 0.3 || Math.abs(distanceToRight) < containerWidth * 0.3) {
                        // Button is partially visible but close to edge - center it for better UX
                        targetScrollLeft = buttonLeft - (containerWidth / 2) + (buttonWidth / 2);
                    }
                    
                    // Only scroll if we actually need to move
                    if (Math.abs(targetScrollLeft - currentScrollLeft) > 10) {
                        container.scrollTo({
                            left: Math.max(0, targetScrollLeft),
                            behavior: 'smooth'
                        });
                    }
                }
            };
            
            // Small delay to ensure DOM is fully rendered
            setTimeout(scrollToSelectedPill, 100);
        }
    }, [selectedBetType]);

    // Helper function to get pill label text
    const getPillLabel = (betType) => {
        switch (betType) {
            case 'team': return 'Bet Ideas';
            case 'prop': return 'Prop Ideas';
            case 'historicalMatchup': return 'Historical Matchup';
            case 'gameLogs': return 'Game Logs';
            case 'playerProps': return 'Player Props Analyzer';
            default: return '';
        }
    };

    // Build pill buttons based on available reports
    const pillButtons = [];
    if (isScoutingReportAvailable && !isPGA) {
        pillButtons.push({ 
            label: 'Bet Ideas', 
            onClick: () => setSelectedBetType('team'), 
            selected: selectedBetType === 'team' 
        });
    }
    if (isPropBetReportAvailable) {
        pillButtons.push({ 
            label: 'Prop Ideas', 
            onClick: () => setSelectedBetType('prop'), 
            selected: selectedBetType === 'prop' 
        });
    }
    if (!isPGA && inDepthBetCardMatchupInsights) {
        pillButtons.push({ 
            label: 'Historical Matchup', 
            onClick: () => setSelectedBetType('historicalMatchup'), 
            selected: selectedBetType === 'historicalMatchup' 
        });
    }

    if (!isPGA && inDepthBetCardTeamStats) {
        pillButtons.push({ 
            label: 'Game Logs', 
            onClick: () => setSelectedBetType('gameLogs'), 
            selected: selectedBetType === 'gameLogs' 
        });
    }

    if (!isPGA && canViewPropAnalyzer(betCardData?.sportType)) {
        pillButtons.push({
            label: 'Player Props Analyzer',
            onClick: () => setSelectedBetType('playerProps'),
            selected: selectedBetType === 'playerProps'
        });
    }

    // Only show injuries tab if injuries are supported for this sport and data is available
    const sportType = betCardData?.sportType;
    if (!isPGA && inDepthBetCardMatchupInjuries && isFeatureSupported('injuries', sportType)) {
        pillButtons.push({
            label: 'Injuries',
            onClick: () => setSelectedBetType('injuries'),
            selected: selectedBetType === 'injuries'
        });
    }

    // Render pill button component
    const PillButton = ({ label, onClick, selected }) => (
        <button 
            className={selected ? componentStyles.pillButtonSelected : componentStyles.pillButton} 
            onClick={onClick}
        >
            {label}
        </button>
    );

    // Display appropriate content based on selected bet type and available reports
    const displayScoutingReport = () => {
        if (pillButtons.length === 0) {
            return (
                <p style={{
                    color: 'var(--bloomberg-orange)', 
                    backgroundColor: 'var(--bloomberg-black)', 
                    padding: '10px', 
                    borderRadius: '5px'
                }}>
                    Check back later when the scouting report is completed.
                </p>
            );
        }

        if (selectedBetType === 'team' && isScoutingReportAvailable) {
            return (
                <div>
                    {/* Key Insights Section */}
                    {hasKeyInsights && (
                        <InsightBox betRecommendation={betCardData['scouting_report'] } />
                    )}
                    
                    {/* Bet Recommendation Section */}
                    <BetRecommendation 
                        betRecommendation={{
                            ...betCardData['scouting_report'],
                            _betCardData: betCardData
                        }} 
                    />
                </div>
            );
        }
        
        if (selectedBetType === 'prop' && isPropBetReportAvailable) {
            return <PropBetRecommendation 
                propBetRecommendationData={{
                    ...betCardData['prop_bet_report'],
                    _betCardData: betCardData
                }} 
            />;
        }

        if (selectedBetType === 'historicalMatchup') {
            return <HistoricalMatchup historicalMatchupStats={inDepthBetCardMatchupInsights} sportType={betCardData?.sportType} />;
        }

        if (selectedBetType === 'gameLogs') {
            return <GameLogs inDepthBetCardTeamStats={inDepthBetCardTeamStats} inDepthBetCardMatchupInsights={inDepthBetCardMatchupInsights} sportType={betCardData?.sportType} />;
        }

        if (selectedBetType === 'playerProps') {
            const sportType = betCardData?.sportType || 'Sport';
            return <PlayerPropAnalyzer sportType={sportType} betCardData={betCardData} />;
        }

        if (selectedBetType === 'injuries' && isFeatureSupported('injuries', betCardData?.sportType)) {
            return <InjuryReport inDepthBetCardMatchupInjuries={inDepthBetCardMatchupInjuries} sportType={betCardData?.sportType} />;
        }
    };

    return (
        <div className={componentStyles.betDataSubSection} id="scoutingReport">
            {/* Game Info Card */}
            <GameInfoCard betCardData={betCardData} />
            
            <div className={componentStyles.pillButtonContainer} ref={pillButtonContainerRef}>
                {pillButtons.map(({ label, onClick, selected }) => (
                    <PillButton 
                        key={label}
                        label={label} 
                        onClick={onClick} 
                        selected={selected} 
                    />
                ))}
            </div>
            {betCardData && displayScoutingReport()}
        </div>
    );
};

export default ScoutingReport;
